import { useEffect, useState } from 'react';
import type { Entity } from '@backstage/catalog-model';
import type { EntityFilterQuery } from '@backstage/catalog-client';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import type { FeaturedItem, QetaKind } from './featured-items';
import { buildTechDocsFilter, buildCatalogFilter } from './featured-items';
import { SEARCH_RESULT_TYPE } from './search-tabs';

export type ResolvedEntityItem = {
  type: string;
  entity: Entity;
};

export type ResolvedQetaItem = {
  type: typeof SEARCH_RESULT_TYPE.QETA;
  kind: QetaKind;
  name: string;
  title?: string;
};

export type ResolvedFeaturedItem = ResolvedEntityItem | ResolvedQetaItem;

const ENTITY_FILTERS: Partial<
  Record<string, (name: string, kind: string) => EntityFilterQuery>
> = {
  [SEARCH_RESULT_TYPE.TECHDOCS]: buildTechDocsFilter,
  [SEARCH_RESULT_TYPE.SOFTWARE_CATALOG]: buildCatalogFilter,
  [SEARCH_RESULT_TYPE.TECH_RADAR]: buildCatalogFilter,
};

/**
 * Fetches all featured entities in parallel and returns only the resolved ones.
 * QETA items are always included; entity-based items are included only if the
 * catalog API returns a matching entity.
 */
export const useFeaturedEntities = (
  items: FeaturedItem[],
): ResolvedFeaturedItem[] => {
  const catalogApi = useApi(catalogApiRef);
  const [resolved, setResolved] = useState<ResolvedFeaturedItem[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      const results = await Promise.all(
        items.map(async (item): Promise<ResolvedFeaturedItem | null> => {
          if (item.type === SEARCH_RESULT_TYPE.QETA) {
            return {
              type: item.type,
              kind: item.kind as QetaKind,
              name: item.name,
              title: item.title,
            };
          }

          const buildFilter = ENTITY_FILTERS[item.type];
          if (!buildFilter) {
            return null;
          }

          try {
            const { items: entities } = await catalogApi.getEntities({
              filter: buildFilter(item.name, item.kind),
            });
            return entities[0]
              ? { type: item.type, entity: entities[0] }
              : null;
          } catch {
            return null;
          }
        }),
      );

      if (!cancelled) {
        setResolved(
          results.filter((r): r is ResolvedFeaturedItem => r !== null),
        );
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [items, catalogApi]);

  return resolved;
};
